'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import MuxHLSVideo from '@/components/utils/MuxHSLVideo';

type VideoAssetProps = React.ComponentProps<typeof MuxHLSVideo> & {
  ensureAutoplay?: boolean;
  loadOffset?: number;
  playOffset?: number;
};

const VideoAsset = forwardRef<HTMLVideoElement, VideoAssetProps>(
  function VideoAsset(
    {
      ensureAutoplay = true,
      playbackId,
      loadOffset = 900,
      playOffset = 200,
      ...rest
    },
    ref
  ) {
    const innerRef = useRef<HTMLVideoElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [shouldLoad, setShouldLoad] = useState(false);
    const [isNearViewport, setIsNearViewport] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    // refs que não causam re-render
    const hasUserInteractedRef = useRef(false);
    const tryingPlayRef = useRef(false); // evita várias tentativas simultâneas
    const mountedRef = useRef(true);

    useImperativeHandle(ref, () => innerRef.current as HTMLVideoElement);

    // Tenta tocar com lock simples
    const tryPlay = useCallback(async () => {
      const el = innerRef.current;
      if (!el || !ensureAutoplay || !isLoaded) return;
      if (tryingPlayRef.current) return;
      if (!isNearViewport || document.hidden) return;
      if (!el.paused) return;

      try {
        tryingPlayRef.current = true;
        await el.play();
      } catch {
        // ignore (mobile policy)
      } finally {
        tryingPlayRef.current = false;
      }
    }, [ensureAutoplay, isLoaded, isNearViewport]);

    const tryPause = useCallback(() => {
      const el = innerRef.current;
      if (!el || !ensureAutoplay) return;
      if (!el.paused && (!isNearViewport || document.hidden)) {
        el.pause();
      }
    }, [ensureAutoplay, isNearViewport]);

    // Observer para carregar (uma vez)
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const loadObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            loadObserver.disconnect();
          }
        },
        { rootMargin: `${loadOffset}px` }
      );

      loadObserver.observe(container);
      return () => loadObserver.disconnect();
    }, [loadOffset]);

    // Observer para tocar/pausar (permanente)
    useEffect(() => {
      const container = containerRef.current;
      if (!container || !ensureAutoplay) return;

      const playObserver = new IntersectionObserver(
        ([entry]) => setIsNearViewport(entry.isIntersecting),
        { rootMargin: `${playOffset}px` }
      );

      playObserver.observe(container);
      return () => playObserver.disconnect();
    }, [playOffset, ensureAutoplay]);

    // Setup do elemento e listeners
    useEffect(() => {
      if (!ensureAutoplay || !shouldLoad) return;
      const el = innerRef.current;
      if (!el) return;

      el.muted = true;
      el.defaultMuted = true;
      el.autoplay = false;
      el.playsInline = true;
      el.controls = false;
      el.setAttribute('playsinline', '');
      el.setAttribute('webkit-playsinline', '');
      el.setAttribute('preload', 'metadata');
      el.disableRemotePlayback = true;
      el.setAttribute('disablepictureinpicture', '');

      const onLoadedData = () => setIsLoaded(true);

      const onVis = () => {
        if (document.hidden) tryPause();
        else tryPlay();
      };

      const userKick = () => {
        hasUserInteractedRef.current = true;
        // tentar tocar assim que houver gesto
        void tryPlay();
        window.removeEventListener('touchstart', userKick, true);
        window.removeEventListener('click', userKick, true);
      };

      el.addEventListener('loadeddata', onLoadedData);
      document.addEventListener('visibilitychange', onVis);
      if (!hasUserInteractedRef.current) {
        window.addEventListener('touchstart', userKick, {
          capture: true,
          passive: true,
        } as any);
        window.addEventListener('click', userKick, true);
      }

      return () => {
        el.removeEventListener('loadeddata', onLoadedData);
        document.removeEventListener('visibilitychange', onVis);
        window.removeEventListener('touchstart', userKick, true as any);
        window.removeEventListener('click', userKick, true);
      };
    }, [shouldLoad, ensureAutoplay, tryPlay, tryPause]);

    // Reagir a interseção e carregamento
    useEffect(() => {
      if (!ensureAutoplay || !isLoaded) return;
      if (isNearViewport) void tryPlay();
      else tryPause();
    }, [isNearViewport, isLoaded, ensureAutoplay, tryPlay, tryPause]);

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    return (
      <div ref={containerRef} className="w-full h-full relative bg-black">
        {shouldLoad && (
          <div className={`w-full h-full transition-opacity duration-700 ease-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
            <MuxHLSVideo
              ref={innerRef}
              playbackId={playbackId}
              className={rest.className || 'w-full h-full'}
              {...rest}
            />
          </div>
        )}
      </div>
    );
  }
);

export default VideoAsset;
